import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy | Trademark Name Registry";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white pt-32 pb-20">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6 text-center">Privacy Policy</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-12"></div>

          <Card className="border-2 border-blue-100 shadow-lg">
            <CardContent className="p-8 md:p-12 space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Introduction</h2>
                <p className="text-gray-700 leading-relaxed">
                  Trademark Name Registry ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our trademark registration services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Information We Collect</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We collect information that you provide directly to us when you:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Fill out contact forms or request information about our services</li>
                  <li>Register for an account or engage our trademark registration services</li>
                  <li>Communicate with us via email, phone, or other means</li>
                  <li>Subscribe to our newsletters or marketing communications</li>
                  <li>Participate in surveys, contests, or promotions</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  The types of personal information we may collect include your name, email address, phone number, mailing address, business information, trademark details, payment information, and any other information you choose to provide.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect for various purposes, including to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Provide, maintain, and improve our trademark registration services</li>
                  <li>Process your trademark applications and communicate with the USPTO on your behalf</li>
                  <li>Send you technical notices, updates, security alerts, and support messages</li>
                  <li>Respond to your comments, questions, and customer service requests</li>
                  <li>Send you marketing communications about our services, promotions, and events (with your consent)</li>
                  <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
                  <li>Detect, prevent, and address technical issues and fraudulent activity</li>
                  <li>Comply with legal obligations and enforce our terms and conditions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Information Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may share your information in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Service Providers:</strong> We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, and customer service.</li>
                  <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, government agencies).</li>
                  <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business.</li>
                  <li><strong>USPTO and Government Agencies:</strong> We will share necessary information with the United States Patent and Trademark Office and other relevant government agencies as required to process your trademark applications.</li>
                  <li><strong>With Your Consent:</strong> We may share your information for any other purpose with your consent.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Data Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  We implement appropriate technical and organizational security measures designed to protect your personal information from unauthorized access, use, or disclosure. However, please note that no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security. You are responsible for maintaining the confidentiality of your account credentials and for any activities that occur under your account.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Data Retention</h2>
                <p className="text-gray-700 leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. The criteria we use to determine retention periods include the nature of the information, the purposes for which we collected it, legal obligations, and whether the information is necessary for our legitimate business interests.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Your Privacy Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on your location, you may have certain rights regarding your personal information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>The right to access and receive a copy of your personal information</li>
                  <li>The right to request correction of inaccurate or incomplete information</li>
                  <li>The right to request deletion of your personal information</li>
                  <li>The right to object to or restrict certain processing of your information</li>
                  <li>The right to data portability</li>
                  <li>The right to withdraw consent (where processing is based on consent)</li>
                  <li>The right to opt-out of marketing communications</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  To exercise these rights, please contact us using the contact information provided below. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may use cookies, web beacons, and other tracking technologies to collect information about your browsing activities and preferences. Cookies are small data files stored on your device. You can control cookie settings through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Third-Party Links</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our website may contain links to third-party websites and services. We are not responsible for the privacy practices or content of these third parties. We encourage you to read the privacy policies of any third-party sites you visit.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete such information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
                </p>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <p className="text-gray-700"><strong>Trademark Name Registry</strong></p>
                  <p className="text-gray-700">1150 S Olive St 10th Floor</p>
                  <p className="text-gray-700">Los Angeles, CA 90015</p>
                  <p className="text-gray-700 mt-2">Email: info@trademarknameregistry.com</p>
                  <p className="text-gray-700">Phone: +1 (123) 123-4567</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">California Privacy Rights</h2>
                <p className="text-gray-700 leading-relaxed">
                  If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA). These include the right to know what personal information we collect, use, disclose, and sell; the right to request deletion of your personal information; the right to opt-out of the sale of your personal information; and the right to non-discrimination for exercising your CCPA rights. We do not sell personal information to third parties.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">International Users</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our services are based in the United States and are governed by U.S. law. If you are accessing our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located. By using our services, you consent to the transfer of your information to the United States and the processing of your information in accordance with this Privacy Policy.
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
